import React from 'react';
import { Bus, Camera, MapPin, Shirt, Sparkles } from 'lucide-react';
import { Card, Field, FieldGrid } from './Card';
import CopyButton from './CopyButton';
import Toggle from './Toggle';

const GENDERS = { 1: 'Male', 2: 'Female', 3: 'Non-Binary' };

const initials = (firstName, lastName) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

// Display only: grades are canonical gradeArr strings. Numbered grades read "Grade 4th"; the others
// (Pre-school, Kindergarten, the over-18 entry) are shown as they are.
const gradeLabel = grade => (grade ? (/^\d/.test(grade) ? `Grade ${grade}` : grade) : '');

/**
 * `commuter` is null when the program has no commuter option. Otherwise:
 *   { value, editable, onToggle, auditCount, onShowAudits }
 * `editable` is false for by-date programs, which show the value read-only (as before).
 */
const ParticipantCard = ({ camper, age, activityInterest, commuter }) => {
    const name = [camper?.firstName, camper?.lastName].filter(Boolean).join(' ');
    const address = [
        camper?.address?.trim(),
        camper?.apartment?.trim(),
        camper?.city?.trim(),
        [camper?.state, camper?.zipCode].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ');
    const subtitle = [age || age === 0 ? `Age ${age}` : '', gradeLabel(camper?.currentGrade)].filter(Boolean).join(' · ');
    const gender = GENDERS[camper?.gender];

    return (
        <Card id="participant" title="Participant">
            <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    {camper?.profileImage
                        ? <img src={camper.profileImage} alt="" className="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-full tw-object-cover" />
                        : <div className="tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-600 tw-text-lg tw-font-semibold tw-text-white" aria-hidden="true">{initials(camper?.firstName, camper?.lastName)}</div>}
                    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">{name}</span>
                        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-sm">
                            {subtitle ? <span className="tw-text-neutral-500">{subtitle}</span> : null}
                            {gender ? <span className="tw-text-neutral-800">{gender}</span> : null}
                        </span>
                    </div>
                </div>

                <FieldGrid className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-3">
                    <Field icon={Shirt} label="T-shirt size">{camper?.tshirtSize}</Field>
                    <Field icon={Shirt} label="Shorts size">{camper?.shortsSize}</Field>
                    <Field icon={Camera} label="Photo / video consent" tone={camper?.allowDocumentCamper === 1 ? 'success' : 'danger'}>
                        {({ 1: 'Granted', 2: 'Not granted' })[Number(camper?.allowDocumentCamper)] ?? null}
                    </Field>
                    {activityInterest !== undefined
                        ? <Field icon={Sparkles} label="Activity interest">{activityInterest}</Field>
                        : null}
                </FieldGrid>

                <Field
                    icon={MapPin}
                    label="Address"
                    className="tw-border-t tw-border-neutral-100 tw-pt-3"
                    aside={<CopyButton value={address} label="address" />}
                >
                    {address}
                </Field>

                {commuter ? (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-neutral-100 tw-pt-3">
                        <Field icon={Bus} label="Commuter">
                            {commuter.value ? 'Yes' : 'No'}
                        </Field>
                        <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
                            {commuter.editable
                                ? <Toggle checked={commuter.value} onChange={commuter.onToggle} label="Commuter" />
                                : null}
                            {commuter.auditCount > 0
                                ? <button type="button" onClick={commuter.onShowAudits} className="tw-text-xs tw-font-medium tw-text-brand-600 hover:tw-text-brand-700 hover:tw-underline">
                                    Commuter audits ({commuter.auditCount})
                                </button>
                                : null}
                        </div>
                    </div>
                ) : null}
            </div>
        </Card>
    );
};

export default ParticipantCard;
